import React, { useEffect, useState } from 'react';

import { Map } from 'immutable';
import { connect } from 'react-redux';
import {
  OnboardingSubtitle,
  OnboardingTitle,
} from 'registration/containers/Onboarding';
import validateTalentProfile, {
  flattenValidationErrors,
} from 'registration/helpers/validateTalentProfile';
import { OnboardingUpdates } from 'registration/types';

import { Input } from 'shared/components/ihcl/input';
import { Stack } from 'shared/components/ihcl/patterns';
import { Select } from 'shared/components/ihcl/select';
import { styled } from 'shared/components/ihcl/styled';
import { Text } from 'shared/components/ihcl/text';
import { Spinner } from 'shared/components/ihcl/v2/spinner';
import ZipCodeInput from 'shared/components/ZipCodeInput';
import { trackClick } from 'shared/helpers/tracking';
import { getTechJobDescriptions } from 'shared/selectors';

import { useOnboardingStepContext } from '../providers/OnboardingStepProvider';
import { useOnboardingVariantContext } from '../providers/OnboardingVariantProvider';
import StickySubmitFooter from './StickySubmitFooter';

const PHONE_ERROR_KEYS = ['phone', 'talent_profile.phone'];

const InputWrapper = styled('div', {
  marginBottom: '20px',
  textAlign: 'left',
});

const SignInPrompt = () => {
  const signInUrl = '/users/sign_in';
  return (
    <Stack $flexDirection="column">
      <Text variant="DisplaySmall" color="grayLabel">
        Already have an account?
      </Text>
      <Text variant="DisplaySmall">
        <a href={signInUrl}>Sign In</a>
      </Text>
    </Stack>
  );
};

const getTitles = (intent) => {
  let header;
  let prompt;
  switch (intent) {
    case 'salary':
      header = 'Technician and technologist salary estimator';
      prompt = 'Get custom salary estimates and much more';
      break;

    default:
      console.error(`Unknown intent: ${intent}`);
  }
  return [header, prompt];
};

const validate = (localStepTalentProfile, talentProfileKeys: string[]) => {
  // Special case zip code and phone errors
  const requiredKeys = talentProfileKeys.filter(
    (k) => k !== 'currentLocation' && k !== 'phone'
  );

  return flattenValidationErrors(
    validateTalentProfile(
      localStepTalentProfile,
      talentProfileKeys,
      requiredKeys
    )
  );
};

type OnboardingTechDetailsFormProps = {
  bodyText?: string;
  includeSignInLink?: boolean;
  pageTalentProfileKeys: string[];
  serverErrors: Map<string, any>;
  techJobDescriptions: ReturnType<typeof getTechJobDescriptions>;
  title?: string;
};
const OnboardingTechDetailsForm = ({
  bodyText = null,
  includeSignInLink = true,
  techJobDescriptions,
  serverErrors,
  pageTalentProfileKeys,
  title = null,
}: OnboardingTechDetailsFormProps) => {
  const {
    addTalentProfileKeys,
    maybeGoToNext,
    isUpdatingProfile,
    localStepTalentProfile,
    setLocalStepTalentProfile,
  } = useOnboardingStepContext();
  const { intent } = useOnboardingVariantContext();
  const [errors, setErrors] = useState(Map<string, string>());
  const [locationHasChanged, setLocationHasChanged] = useState(false);
  const [locationIsLoading, setLocationIsLoading] = useState(false);

  useEffect(() => {
    addTalentProfileKeys(pageTalentProfileKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setErrors((prevErrors) => {
      let newErrorMap = prevErrors;

      PHONE_ERROR_KEYS.forEach((phoneErrorKey) => {
        if (Object.keys(serverErrors).includes(phoneErrorKey)) {
          newErrorMap = Map(
            prevErrors.set('phone', serverErrors[phoneErrorKey][0])
          );
        }
      });

      return newErrorMap;
    });
  }, [serverErrors]);

  if (Object.keys(localStepTalentProfile).length === 0) {
    return null;
  }

  const { currentLocation, fullName, phone, techRoleOptions } =
    localStepTalentProfile;

  const submit = () => {
    const newErrors = validate(localStepTalentProfile, pageTalentProfileKeys);
    if (newErrors.size > 0) {
      setErrors(newErrors);
      return;
    }

    trackClick('talent.onboarding.submit', {
      Intent: intent,
    });
    maybeGoToNext();
  };

  const genHandleValueChange = (name) => (change) => {
    let value;
    const newTalentProfile: {
      [key: string]: any;
    } = {};

    switch (name) {
      case 'fullName':
      case 'phone':
        ({ value } = change.currentTarget);
        break;
      case 'techRoleOptions':
        value = change.value.map((choice) => ({
          id: choice.value,
          title: choice.label,
        }));
        break;

      default:
      // pass
    }
    newTalentProfile[name] = value;

    setLocalStepTalentProfile((prevLocalStepTalentProfile) => ({
      ...prevLocalStepTalentProfile,
      ...newTalentProfile,
    }));
    setErrors((prevErrors) => prevErrors.delete(name));
  };

  let zipCodeDetails = null;
  if (locationIsLoading || locationHasChanged) {
    zipCodeDetails = (
      <>
        {locationIsLoading && (
          <div>
            <Spinner className="mr-1 inline-block size-4 align-middle" />{' '}
            Loading
          </div>
        )}

        {locationHasChanged &&
          currentLocation &&
          !currentLocation.isEmpty() && (
            <div>
              {currentLocation.city}, {currentLocation.state}
            </div>
          )}
      </>
    );
  }

  const techJobDescriptionsOptions = techJobDescriptions.map(
    (techJobDescription) => ({
      value: techJobDescription.id,
      label: techJobDescription.title,
    })
  );

  const techRoleOptionValues = (techRoleOptions || []).map((role) => role.id);
  const selectedTechDescriptionOptions = techJobDescriptionsOptions.filter(
    (techJobDescription) =>
      techRoleOptionValues.includes(techJobDescription.value)
  );

  const fullNameField = (
    <InputWrapper>
      <Input
        id="full-name"
        onChange={genHandleValueChange('fullName')}
        label="Full name"
        value={fullName}
        error={Boolean(errors.get('fullName'))}
        caption={errors.get('fullName')}
      />
    </InputWrapper>
  );

  const handleCurrentLocationChange = (newLocation, isLoading) => {
    const additionalFields = {} as Pick<
      OnboardingUpdates,
      'other_locations_attributes'
    >;
    if (intent === 'resume') {
      additionalFields.other_locations_attributes = [
        {
          city: newLocation.city,
          state: newLocation.state,
          zip_code: newLocation.zipCode,
        },
      ];
    }
    setLocalStepTalentProfile((prevLocalStepTalentProfile) => ({
      ...prevLocalStepTalentProfile,
      currentLocation: newLocation,
      ...additionalFields,
    }));
    setErrors((prevErrors) => prevErrors.delete('currentLocation'));
    setLocationHasChanged(true);
    setLocationIsLoading(isLoading);
  };
  const zipCodeField = (
    <InputWrapper>
      <ZipCodeInput
        id="zip-code"
        onChange={handleCurrentLocationChange}
        place={currentLocation}
        label="Zipcode"
        useIhcl
        error={Boolean(errors.get('currentLocation'))}
        caption={errors.get('currentLocation')}
      />
      {zipCodeDetails}
    </InputWrapper>
  );

  const phoneField = (
    <InputWrapper>
      <Input
        id="phone"
        onChange={genHandleValueChange('phone')}
        label="Phone number"
        type="tel"
        value={phone}
        error={Boolean(errors.get('phone'))}
        caption={errors.get('phone')}
      />
    </InputWrapper>
  );

  const techJobDescriptionsField = (
    <InputWrapper>
      <Select
        multi
        clearable={false}
        instanceId="tech-job-descriptions"
        onChange={genHandleValueChange('techRoleOptions')}
        options={techJobDescriptionsOptions}
        placeholder="Select tech job descriptions"
        value={selectedTechDescriptionOptions}
        valueKey="value"
        error={Boolean(errors.get('techRoleOptions'))}
        caption={<>{errors.get('techRoleOptions')}</>}
      />
    </InputWrapper>
  );

  const [header, prompt] = getTitles(intent);
  return (
    <>
      <div>
        <OnboardingTitle>{title || header}</OnboardingTitle>
        <OnboardingSubtitle $fontWeight="500">
          {bodyText || prompt}
        </OnboardingSubtitle>

        {pageTalentProfileKeys.includes('fullName') && fullNameField}
        {pageTalentProfileKeys.includes('phone') && phoneField}
        {pageTalentProfileKeys.includes('currentLocation') && zipCodeField}
        {pageTalentProfileKeys.includes('techRoleOptions') &&
          techJobDescriptionsField}
        {includeSignInLink && <SignInPrompt />}
      </div>

      <StickySubmitFooter
        onClick={submit}
        disabled={isUpdatingProfile || (errors && errors.size > 0)}
        isLoading={isUpdatingProfile}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  serverErrors: state.serverErrors,
  techJobDescriptions: getTechJobDescriptions(state),
});

export default connect(mapStateToProps)(OnboardingTechDetailsForm);
